import { Check } from "lucide-react";
import { REMAINING_PCT } from "@/lib/inventory";

type StageStatus = "done" | "active" | "upcoming";

type Stage = {
  key: string;
  label: string;
  sub: string;
  status: StageStatus;
};

// Capacity-based waves — no public dates, no price talk (prices hidden by design).
// Final wave framing: this is the last allocation, not a step before a price rise.
const stages: Stage[] = [
  { key: "early", label: "Early Bird", sub: "Sold Out", status: "done" },
  { key: "wave1", label: "Wave 1", sub: "Sold Out", status: "done" },
  { key: "wave2", label: "Final Wave", sub: `⚠️ Less Than ${REMAINING_PCT}% Remaining`, status: "active" },
];

const PricingStageTimeline = () => {
  return (
    <section className="bg-yacht-cream py-10 md:py-14 px-6 border-y border-yacht-charcoal/10">
      <div className="max-w-3xl mx-auto">
        <p className="text-yacht-blue text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-center mb-2">
          ★ Last Call ★
        </p>
        <p className="text-yacht-charcoal/55 text-xs md:text-sm text-center mb-7">
          This is the final wave. When it sells through, that's everyone.
        </p>

        <div className="relative">
          {/* Background line */}
          <div className="absolute top-[10px] md:top-3 h-0.5 bg-yacht-charcoal/15 z-0" style={{ left: "16.66%", right: "16.66%" }} />

          {/* Active progress fill — reaches the final (active) dot */}
          <div
            className="absolute top-[10px] md:top-3 h-0.5 bg-yacht-blue z-0 transition-all duration-500"
            style={{ left: "16.66%", width: "66.68%" }}
          />

          {/* Stage dots */}
          <div className="relative grid grid-cols-3">
            {stages.map((s) => (
              <div key={s.key} className="flex flex-col items-center text-center px-1">
                <div className="relative w-5 h-5 md:w-6 md:h-6 z-10">
                  {s.status === "done" && (
                    <div className="w-full h-full rounded-full bg-yacht-blue flex items-center justify-center">
                      <Check className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" strokeWidth={3} />
                    </div>
                  )}
                  {s.status === "active" && (
                    <>
                      <span className="absolute inset-0 rounded-full bg-yacht-yellow animate-ping opacity-50" />
                      <div className="relative w-full h-full rounded-full bg-yacht-yellow ring-[3px] md:ring-4 ring-yacht-yellow/25" />
                    </>
                  )}
                  {s.status === "upcoming" && (
                    <div className="w-full h-full rounded-full bg-yacht-cream border-2 border-yacht-charcoal/25" />
                  )}
                </div>
                <p
                  className={`mt-3 text-[10px] md:text-xs font-bold uppercase tracking-[0.15em] ${
                    s.status === "active"
                      ? "text-yacht-charcoal"
                      : s.status === "done"
                      ? "text-yacht-charcoal/65"
                      : "text-yacht-charcoal/40"
                  }`}
                >
                  {s.label}
                </p>
                <p
                  className={`mt-1.5 text-[10px] md:text-[11px] ${
                    s.status === "active"
                      ? "text-yacht-blue font-semibold"
                      : s.status === "done"
                      ? "text-yacht-charcoal/45"
                      : "text-yacht-charcoal/40"
                  }`}
                >
                  {s.sub}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingStageTimeline;
